import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Clear as ClearIcon,
  LockOutlined as LockOutlinedIcon,
  MenuBook as MenuBookIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  AccountBalance as AccountBalanceIcon,
  AttachMoney as AttachMoneyIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import {
  tienePermisosModulo,
  puedeEditarProgramaContabilidad,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, StatCard, fpInputSx } from 'components/financepro';
import PrintListado from 'utils/PrintListado';
import RegistrarEntradaDiario from './RegistrarEntradaDiario';
import { ETIQUETA_VERSION, useVersionContable } from './versionContable';
import VistaEntradaDiario from './VistaEntradaDiario';
import {
  PROGRAMA_ENTRADAS_DIARIO,
  TIPO_DOCUMENTO_AJUSTE_ENTRADAS,
  fmtMoney,
  formatearFecha,
  fechaLocalHoy,
  primerDiaAno,
  cardShellSx,
  filterHeaderSx,
  cellHeadSx,
  btnPrimarySx,
  btnOutlinedSx,
} from './entradaDiarioShared';

// Estado del asiento frente al pase a fiscal (columna estadofiscal).
const ESTADO_FISCAL = { P: 'Pendiente', F: 'Fiscal', X: 'Excluido' };

const EntradasDiario = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const {
    nombreNegocio,
    usuarioLog,
    claveLog,
    roleDetalles,
    direccion_empresa: direccionEmpresaCtx,
    rnc_empresa: rncEmpresaCtx,
  } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = tienePermisosModulo(roleDetalles, PROGRAMA_ENTRADAS_DIARIO);
  const puedeModificar = puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA_ENTRADAS_DIARIO);
  const puedeOperarDb = Boolean(selectedCompany?.db_empresa);
  const { activa: versionesActivas, version, soloConsulta, refrescar: refrescarVersion } =
    useVersionContable();

  const [entradas, setEntradas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [fechaDesde, setFechaDesde] = useState(primerDiaAno);
  const [fechaHasta, setFechaHasta] = useState(fechaLocalHoy);
  const [concepto, setConcepto] = useState('');
  const [numero, setNumero] = useState('');

  const [dialogoNueva, setDialogoNueva] = useState(false);
  const [vistaAbierta, setVistaAbierta] = useState(false);
  const [entradaVista, setEntradaVista] = useState({});
  const [autoPrint, setAutoPrint] = useState(false);

  const datosEmpresa = useMemo(() => {
    const nombreCtx =
      typeof nombreNegocio === 'string' && nombreNegocio.trim() && nombreNegocio !== 'false'
        ? nombreNegocio.trim()
        : '';
    return {
      nombre: selectedCompany?.name || selectedCompany?.empresa || nombreCtx || 'Empresa',
      direccion: typeof direccionEmpresaCtx === 'string' ? direccionEmpresaCtx.trim() : '',
      rnc: typeof rncEmpresaCtx === 'string' ? rncEmpresaCtx.trim() : '',
      telefono: '',
      email: '',
    };
  }, [selectedCompany, nombreNegocio, direccionEmpresaCtx, rncEmpresaCtx]);

  const credencialesBase = useCallback(
    () => ({
      id_empresa: selectedCompany,
      usuario: usuarioLog || '',
      password: claveLog || '',
      version,
    }),
    [selectedCompany, usuarioLog, claveLog, version]
  );

  const cargarEntradas = useCallback(async (opts = {}) => {
    if (!puedeLeer || !puedeOperarDb) return;
    setCargando(true);
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_listarentradasdiario`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credencialesBase(),
          documento: TIPO_DOCUMENTO_AJUSTE_ENTRADAS,
          fecha_desde: (fechaDesde || '').trim() || undefined,
          fecha_hasta: (fechaHasta || '').trim() || undefined,
          concepto: (concepto || '').trim() || undefined,
          numero: (numero || '').trim() || undefined,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'Error al cargar las entradas de diario');
        setEntradas([]);
        return;
      }
      const lista = data.entradas || [];
      setEntradas(lista);
      if (lista.length === 0 && !opts.silencioso) {
        showSuccess('No se encontraron asientos con los filtros aplicados');
      }
    } catch {
      showError('Error de conexión al cargar las entradas de diario');
      setEntradas([]);
    } finally {
      setCargando(false);
    }
  }, [
    puedeLeer,
    puedeOperarDb,
    servidor,
    credencialesBase,
    fechaDesde,
    fechaHasta,
    concepto,
    numero,
    showError,
    showSuccess,
  ]);

  useEffect(() => {
    if (puedeLeer && puedeOperarDb) cargarEntradas({ silencioso: true });
    // Carga inicial al entrar o cambiar de empresa; la búsqueda se aplica con «Buscar»
  }, [puedeLeer, puedeOperarDb, selectedCompany?.db_empresa, version]);

  const obtenerDetalle = useCallback(
    async (fila) => {
      const response = await fetch(`${servidor}/contabilidadbackend_obtenerentradadiario`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credencialesBase(),
          documento: fila.documento || TIPO_DOCUMENTO_AJUSTE_ENTRADAS,
          numero: fila.numero,
          ano: fila.ano,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.Error || 'Error al obtener el asiento');
      }
      return data.entrada || {};
    },
    [servidor, credencialesBase]
  );

  const verEntrada = async (fila, imprimir = false) => {
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    try {
      const entrada = await obtenerDetalle(fila);
      setEntradaVista(entrada);
      setAutoPrint(Boolean(imprimir));
      setVistaAbierta(true);
    } catch (e) {
      showError(e.message || 'Error al obtener el asiento');
    }
  };

  const abrirNueva = () => {
    if (!puedeModificar) {
      showError('No tiene permisos para registrar asientos.');
      return;
    }
    setDialogoNueva(true);
  };

  const onGuardado = (entrada) => {
    setDialogoNueva(false);
    refrescarVersion(); // un asiento nuevo suma a los pendientes de pasar
    setEntradaVista(entrada || {});
    setAutoPrint(false);
    setVistaAbierta(true);
    cargarEntradas();
  };

  const handleLimpiar = () => {
    setFechaDesde(primerDiaAno());
    setFechaHasta(fechaLocalHoy());
    setConcepto('');
    setNumero('');
  };

  const totalDebito = useMemo(
    () => entradas.reduce((s, e) => s + (parseFloat(e.total_debito) || 0), 0),
    [entradas]
  );
  const totalCredito = useMemo(
    () => entradas.reduce((s, e) => s + (parseFloat(e.total_credito) || 0), 0),
    [entradas]
  );

  const columnasPrint = useMemo(
    () => [
      { id: 'documento', label: 'Documento' },
      { id: 'numero', label: 'Nº' },
      { id: 'fecha', label: 'Fecha', align: 'center', render: (v) => formatearFecha(v) },
      { id: 'concepto', label: 'Concepto' },
      {
        id: 'total_debito',
        label: 'Débito',
        align: 'right',
        render: (v) => `$${fmtMoney(v)}`,
      },
      {
        id: 'total_credito',
        label: 'Crédito',
        align: 'right',
        render: (v) => `$${fmtMoney(v)}`,
      },
      { id: 'creado', label: 'Usuario' },
    ],
    []
  );

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Entradas de diario"
          subtitle={nombreNegocio ? `${nombreNegocio} · Ajuste Entradas` : 'Ajuste Entradas'}
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «entradasdiario» en roles para consultar asientos.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  if (!puedeOperarDb) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Entradas de diario"
          subtitle={nombreNegocio ? `${nombreNegocio} · Ajuste Entradas` : 'Ajuste Entradas'}
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Alert severity="info">Seleccione una empresa con base de datos asignada.</Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Entradas de diario"
        subtitle={[
          nombreNegocio,
          TIPO_DOCUMENTO_AJUSTE_ENTRADAS,
          versionesActivas ? `Versión ${ETIQUETA_VERSION[version].toLowerCase()}` : '',
        ]
          .filter(Boolean)
          .join(' · ')}
        cta={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={abrirNueva}
            disabled={!puedeModificar || !usuarioLog || soloConsulta}
            title={soloConsulta ? 'La versión fiscal es solo de consulta' : undefined}
            sx={{ ...btnPrimarySx, height: 42 }}
          >
            Nueva entrada
          </Button>
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={4}>
            <StatCard
              label="Asientos"
              value={String(entradas.length)}
              icon={ReceiptLongIcon}
              accent={FP.color.navy}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              label="Total débito"
              value={`$${fmtMoney(totalDebito)}`}
              icon={AccountBalanceIcon}
              accent={FP.color.accentBlue}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              label="Total crédito"
              value={`$${fmtMoney(totalCredito)}`}
              icon={AttachMoneyIcon}
              accent={FP.color.success}
            />
          </Grid>
        </Grid>

        <Card elevation={0} sx={{ ...cardShellSx, mb: 2.5 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MenuBookIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Filtros de búsqueda
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Box className="no-print">
              <PrintListado
                title={datosEmpresa.nombre}
                empresa={datosEmpresa}
                reportTitle={`Entradas de diario · ${TIPO_DOCUMENTO_AJUSTE_ENTRADAS}`}
                subtitle={
                  [fechaDesde, fechaHasta].filter(Boolean).length
                    ? `Del ${fechaDesde || '...'} al ${fechaHasta || '...'}`
                    : undefined
                }
                columns={columnasPrint}
                data={entradas}
                emptyMessage="No hay asientos para imprimir"
                printButtonLabel="Imprimir listado"
                printButtonProps={{
                  sx: {
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 1.4,
                    bgcolor: FP.color.navy,
                    color: '#FFFFFF',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                  },
                }}
                footer={`Asientos: ${entradas.length} | Débito: $${fmtMoney(totalDebito)} | Crédito: $${fmtMoney(
                  totalCredito
                )}`}
              />
            </Box>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }} className="no-print">
            <Grid container spacing={2.5} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Número"
                  value={numero}
                  onChange={(e) => setNumero(e.target.value.replace(/\D/g, ''))}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Concepto"
                  value={concepto}
                  onChange={(e) => setConcepto(e.target.value)}
                  placeholder="Buscar en concepto"
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={cargando ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                    onClick={cargarEntradas}
                    disabled={cargando}
                    sx={{ ...btnPrimarySx, flex: 1 }}
                  >
                    Buscar
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleLimpiar}
                    sx={{ ...btnOutlinedSx, flex: 1 }}
                  >
                    Limpiar
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card elevation={0} sx={cardShellSx}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Detalle de entradas de diario
            </Typography>
            {cargando && (
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', fontWeight: 600 }}>
                Cargando…
              </Typography>
            )}
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            {cargando ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} sx={{ color: FP.color.accentBlue }} />
              </Box>
            ) : entradas.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <ReceiptLongIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                  Use los filtros y pulse «Buscar» para listar las entradas de diario.
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
              >
                <Table sx={{ tableLayout: 'fixed', width: '100%', minWidth: { xs: 1100, sm: 1280 } }}>
                  <colgroup>
                    {['14%', '6%', '9%', '22%', '10%', '10%', '10%', '10%', '9%'].map((w, i) => (
                      <col key={i} style={{ width: w }} />
                    ))}
                  </colgroup>
                  <TableBody>
                    <TableRow
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        '& td': {
                          border: 'none',
                          borderBottom: `2px solid ${FP.color.border}`,
                          bgcolor: FP.color.surfaceAlt,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Documento
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Nº
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Fecha
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Concepto
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                          Débito
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                          Crédito
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Usuario
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Estado
                        </Typography>
                      </TableCell>
                      <TableCell className="no-print">
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Acciones
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {entradas.map((e) => (
                      <TableRow key={`${e.ano}-${e.documento}-${e.numero}`} hover>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={e.documento || '—'}
                        >
                          {e.documento || '—'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: FP.color.textPrimary }}>{e.numero}</TableCell>
                        <TableCell align="center">{formatearFecha(e.fecha)}</TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={e.concepto || '—'}
                        >
                          {e.concepto || '—'}
                        </TableCell>
                        <TableCell align="right">${fmtMoney(e.total_debito)}</TableCell>
                        <TableCell align="right">${fmtMoney(e.total_credito)}</TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={e.creado || '—'}
                        >
                          {e.creado || '—'}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={e.cuadrada ? 'Cuadrada' : 'Descuadrada'}
                            sx={{
                              fontWeight: 700,
                              bgcolor: e.cuadrada ? FP.color.successSoft : FP.color.dangerSoft,
                              color: e.cuadrada ? FP.color.successDark : FP.color.danger,
                            }}
                          />
                          {versionesActivas && !soloConsulta && ESTADO_FISCAL[e.estadofiscal] && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={ESTADO_FISCAL[e.estadofiscal]}
                              sx={{ ml: 0.5, fontWeight: 600 }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center" className="no-print">
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              justifyContent: 'center',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                            }}
                          >
                            <Tooltip title="Ver">
                              <IconButton
                                size="small"
                                onClick={() => verEntrada(e, false)}
                                sx={{
                                  color: FP.color.navy,
                                  '&:hover': { bgcolor: FP.color.surfaceAlt },
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reimprimir">
                              <IconButton
                                size="small"
                                onClick={() => verEntrada(e, true)}
                                sx={{
                                  color: FP.color.accentBlue,
                                  '&:hover': { bgcolor: FP.color.accentBlueSoft },
                                }}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      <RegistrarEntradaDiario
        open={dialogoNueva}
        onClose={() => setDialogoNueva(false)}
        onGuardado={onGuardado}
      />
      <VistaEntradaDiario
        open={vistaAbierta}
        onClose={() => {
          setVistaAbierta(false);
          setAutoPrint(false);
        }}
        entrada={entradaVista}
        autoPrint={autoPrint}
      />
    </Box>
  );
};

export default EntradasDiario;
